import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Box,
  FormControl,
  Select,
  Pagination,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import StartIcon from "@mui/icons-material/Start";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiBadge from "components/SuiBadge";

import useUserRoles from "hooks/useUserRoles";

import PlanesPagoApi from "api/planesPagoApi";
import DatosBasicosApi from "api/datosbasicosApi";
import SoportesPlanPagoModal from "./soportesPlanPago";
import IniciarPlanPago from "./iniciarPlanPago";
import BalanceModal from "./movimientosModal";

const PlanesPagoTable = () => {
  const { user, roles } = useUserRoles();

  // Estados para paginación del servidor
  const [planesPago, setPlanesPago] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);

  // Estados para filtros del servidor
  const [searchTerm, setSearchTerm] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [autorizadoFiltro, setAutorizadoFiltro] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [procesoEstadoFiltro, setProcesoEstadoFiltro] = useState("");

  // Estados para opciones de filtros
  const [estadosDisponibles, setEstadosDisponibles] = useState([]);

  // Estados generales
  const [loading, setLoading] = useState(true);

  // Estados del menú y modales
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedPlanPago, setSelectedPlanPago] = useState(null);
  const [modalIniciarPlan, setModalIniciarPlan] = useState(false);
  const [modalMovimientosOpen, setModalMovimientosOpen] = useState(false);
  const [modalSoportesOpen, setModalSoportesOpen] = useState(false);

  const hasFetched = useRef(false);
  const searchTimeoutRef = useRef(null);

  // FUNCIÓN PRINCIPAL PARA CARGAR DATOS
  const fetchPlanesPago = useCallback(
    async (page, pageSize, search, estado, autorizado, tipo, procesoEstado) => {
      console.log(`💳 Cargando planes de pago - Página: ${page}`);
      setLoading(true);
      try {
        const params = { page, page_size: pageSize };

        // Solo agregar parámetros no vacíos
        if (search && search.trim()) params.search = search.trim();
        if (estado) params.estado = estado;
        if (autorizado) params.autorizado = autorizado;
        if (tipo) params.tipo = tipo;
        if (procesoEstado) params.proceso_estado = procesoEstado;

        const response = await PlanesPagoApi.fetchPlanesPagoPaginacion(params);
        setPlanesPago(response.data.results);
        setTotalItems(response.data.pagination.total_items);
        setTotalPages(response.data.pagination.total_pages);
      } catch (error) {
        console.error("Error al obtener los Planes de Pago:", error);
        setPlanesPago([]);
        setTotalItems(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Cargar filtros disponibles
  const cargarFiltros = useCallback(async () => {
    try {
      const estadosResponse = await DatosBasicosApi.fetchEstadosProceso();
      setEstadosDisponibles(estadosResponse.data);
    } catch (error) {
      console.error("Error al cargar filtros:", error);
    }
  }, []);

  // Effect SOLO para carga inicial
  useEffect(() => {
    if (user && !hasFetched.current) {
      console.log("🚀 Carga inicial");
      hasFetched.current = true;
      cargarFiltros();
      fetchPlanesPago(
        1,
        pageSize,
        searchTerm,
        estadoFiltro,
        autorizadoFiltro,
        tipoFiltro,
        procesoEstadoFiltro
      );
    }
  }, [
    user,
    cargarFiltros,
    fetchPlanesPago,
    pageSize,
    searchTerm,
    estadoFiltro,
    autorizadoFiltro,
    tipoFiltro,
    procesoEstadoFiltro,
  ]);

  // Effect SOLO para cambios de página
  useEffect(() => {
    if (hasFetched.current) {
      console.log(`📄 Navegando a página: ${currentPage}`);
      fetchPlanesPago(
        currentPage,
        pageSize,
        searchTerm,
        estadoFiltro,
        autorizadoFiltro,
        tipoFiltro,
        procesoEstadoFiltro
      );
    }
  }, [currentPage]);

  // Effect para búsqueda con debounce
  useEffect(() => {
    if (!hasFetched.current) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      console.log(`🔍 Búsqueda: "${searchTerm}"`);
      setCurrentPage(1);
      fetchPlanesPago(
        1,
        pageSize,
        searchTerm,
        estadoFiltro,
        autorizadoFiltro,
        tipoFiltro,
        procesoEstadoFiltro
      );
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Effect para filtros
  useEffect(() => {
    if (hasFetched.current) {
      console.log("🎛️ Aplicando filtros");
      setCurrentPage(1);
      fetchPlanesPago(
        1,
        pageSize,
        searchTerm,
        estadoFiltro,
        autorizadoFiltro,
        tipoFiltro,
        procesoEstadoFiltro
      );
    }
  }, [estadoFiltro, autorizadoFiltro, tipoFiltro, procesoEstadoFiltro]);

  // Effect para cambios de pageSize
  useEffect(() => {
    if (hasFetched.current) {
      console.log(`📊 Cambiando tamaño de página: ${pageSize}`);
      setCurrentPage(1);
      fetchPlanesPago(
        1,
        pageSize,
        searchTerm,
        estadoFiltro,
        autorizadoFiltro,
        tipoFiltro,
        procesoEstadoFiltro
      );
    }
  }, [pageSize]);

  // Handlers para filtros del servidor
  const handleEstadoChange = (event) => {
    setEstadoFiltro(event.target.value);
  };

  const handleAutorizadoChange = (event) => {
    setAutorizadoFiltro(event.target.value);
  };

  const handleTipoChange = (event) => {
    setTipoFiltro(event.target.value);
  };

  const handleProcesoEstadoChange = (event) => {
    setProcesoEstadoFiltro(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    console.log(`👆 Usuario hace clic en página: ${newPage}`);
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const limpiarFiltros = () => {
    setSearchTerm("");
    setEstadoFiltro("");
    setAutorizadoFiltro("");
    setTipoFiltro("");
    setProcesoEstadoFiltro("");
    setCurrentPage(1);
  };

  // Función para recargar manteniendo página actual
  const reloadCurrentPage = () => {
    fetchPlanesPago(
      currentPage,
      pageSize,
      searchTerm,
      estadoFiltro,
      autorizadoFiltro,
      tipoFiltro,
      procesoEstadoFiltro
    );
  };

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let planPago = planesPago.find((a) => a.id === id);
    setSelectedPlanPago(planPago);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleIniciarPlan = (planPago) => {
    setSelectedPlanPago(planPago);
    setModalIniciarPlan(true);
    handleMenuClose();
  };

  const handleMovimientos = (planPago) => {
    setSelectedPlanPago(planPago);
    setModalMovimientosOpen(true);
    handleMenuClose();
  };

  const handleSoportes = (planPago) => {
    setSelectedPlanPago(planPago);
    setModalSoportesOpen(true);
    handleMenuClose();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleCambiarEstadoPlan = async (planPago) => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas ${
        planPago.autorizado ? "DESAUTORIZAR" : "AUTORIZAR"
      } este plan de pago?`
    );
    if (confirmAction) {
      const datos = {
        operacion: planPago.autorizado ? "DESAUTORIZAR_PLAN_PAGO" : "AUTORIZAR_PLAN_PAGO",
        id_plan_pago: planPago.id,
      };
      try {
        const response = await PlanesPagoApi.operarPlanPago(datos);
        if (response.status === 201) {
          alert("Estado de Plan de Pago actualizado correctamente");
          reloadCurrentPage();
          handleMenuClose();
        }
      } catch (error) {
        alert(obtenerMensajeError(error, "Error al actualizar el Estado de Plan de Pago"));
      }
    }
  };

  const handleReiniciarPlan = async (planPago) => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas REINICIAR este Plan de Pago (Eliminando sus Movimientos)?`
    );
    if (confirmAction) {
      const datos = {
        operacion: "REINICIAR_PLAN_PAGO",
        id_plan_pago: planPago.id,
        proceso: planPago.proceso,
        estado: planPago.estado,
        tipo: planPago.tipo,
        dia_cobro: planPago.dia_cobro,
        cuotas: planPago.cuotas,
        abono_inicial: 0,
        monto_total: 0,
        fecha_compromiso: planPago.fecha_compromiso,
      };
      try {
        const response = await PlanesPagoApi.operarPlanPago(datos);
        if (response.status === 201 && response.data) {
          alert("Plan de Pago reiniciado éxitosamente.");
          reloadCurrentPage();
          handleMenuClose();
        }
      } catch (error) {
        alert(obtenerMensajeError(error, "Error al reiniciar el Plan de Pago"));
      }
    }
  };

  const handleActualizarPlanPago = (planActualizado) => {
    setSelectedPlanPago(planActualizado);
    reloadCurrentPage();
  };

  const columns = [
    {
      field: "Opciones",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton sx={{ padding: "2px" }} onClick={(e) => handleMenuClick(e, params.row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "cliente_detalle",
      headerName: "Cliente",
      flex: 3,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.cliente_nombre} ({params.row.cliente_identificacion})
        </SuiTypography>
      ),
    },
    {
      field: "proceso",
      headerName: "Proceso",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.proceso_identificador}
        </SuiTypography>
      ),
    },
    {
      field: "estado_proceso",
      headerName: "Estado Proceso",
      flex: 3,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.proceso_estado || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "estado",
      headerName: "Estado Plan",
      sortable: false,
      flex: 3,
      renderCell: (params) => {
        const estado = params.row.estado_plan;
        const isCritico = estado === "NO ACTIVO" || estado === "ACTIVO - NO AUTORIZADO";

        return (
          <SuiTypography
            variant="caption"
            fontWeight="medium"
            sx={{
              backgroundColor: isCritico ? "#ffe0e0" : "transparent", // rojo claro
              color: isCritico ? "#b00020" : "inherit",
              px: 1,
              py: 0.5,
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            {estado}
          </SuiTypography>
        );
      },
    },
    {
      field: "monto_total",
      headerName: "Monto",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.monto_total ? formatCurrency(params.row.monto_total) : "$ 0"}
        </SuiTypography>
      ),
    },
    {
      field: "saldo_pendiente",
      headerName: "Saldo Pendiente",
      flex: 3,
      renderCell: (params) => (
        <SuiBadge
          variant="gradient"
          color={params.row.porcentaje_pagado < 100 ? "error" : "success"}
          size="large"
          badgeContent={
            <span style={{ fontSize: "12px", fontWeight: "bold" }}>
              {formatCurrency(params.row.saldo_pendiente)}
            </span>
          }
          container
        />
      ),
    },
  ];

  return (
    <SuiBox>
      {loading ? (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: "none",
            backgroundColor: "transparent",
          }}
        >
          {/* Header con controles */}
          <SuiBox p={1}>
            <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 1 }}>
              <Grid size={6}>
                <SuiTypography variant="h4" fontWeight="bold">
                  Administración de Planes de Pago ({totalItems})
                </SuiTypography>
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <FormControl size="small">
                    <SuiTypography variant="h6" fontWeight="light">
                      Buscar por...
                    </SuiTypography>
                    <TextField
                      size="small"
                      placeholder="identificador, cliente..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 350 }}
                    />
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <SuiTypography variant="h6" fontWeight="light">
                      Estado Plan
                    </SuiTypography>
                    <Select value={estadoFiltro} label="Estado" onChange={handleEstadoChange}>
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="true">Activo</MenuItem>
                      <MenuItem value="false">Inactivo</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <SuiTypography variant="h6" fontWeight="light">
                      Autorizado
                    </SuiTypography>
                    <Select
                      value={autorizadoFiltro}
                      label="Autorizado"
                      onChange={handleAutorizadoChange}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="true">Sí</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <SuiTypography variant="h6" fontWeight="light">
                      Tipo
                    </SuiTypography>
                    <Select value={tipoFiltro} label="Tipo" onChange={handleTipoChange}>
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="CONTADO">Contado</MenuItem>
                      <MenuItem value="CREDITO">Crédito</MenuItem>
                      <MenuItem value="POR DEFINIR">Por Definir</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <SuiTypography variant="h6" fontWeight="light">
                      Estado Proceso
                    </SuiTypography>
                    <Select
                      value={procesoEstadoFiltro}
                      label="Estado Proceso"
                      onChange={handleProcesoEstadoChange}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {estadosDisponibles.map((estado) => (
                        <MenuItem key={estado.id} value={estado.id}>
                          {estado.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <SuiTypography variant="h6" fontWeight="light">
                      Items x Página
                    </SuiTypography>
                    <Select
                      value={pageSize}
                      label="Tamaño"
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title="Limpiar Filtros" placement="right">
                    <IconButton
                      onClick={limpiarFiltros}
                      disabled={loading}
                      size="large"
                      sx={{ ml: 1 }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </SuiBox>
              </Grid>
            </Grid>
          </SuiBox>

          {/* DataGrid */}
          <DataGrid
            rows={planesPago}
            columns={columns}
            loading={loading}
            getRowHeight={() => "auto"}
            sx={{
              border: 0,
              backgroundColor: "white",
              borderRadius: 2,
              p: 1,
              [`& .${gridClasses.cell}`]: {
                borderBottom: "none",
                padding: "2px 2px",
              },
              [`& .${gridClasses.row}`]: {
                backgroundColor: ({ palette: { white } }) => white.main,
                "&:hover": {
                  backgroundColor: ({ palette: { grey } }) => grey[200],
                },
                minHeight: "12px !important",
              },
              [`& .${gridClasses.columnHeaders}`]: {
                backgroundColor: ({ palette: { grey } }) => grey[50],
                borderRadius: 1,
                minHeight: "12px !important",
              },
              [`& .${gridClasses.footerContainer}`]: {
                display: "none",
              },
            }}
            hideFooter
          />

          {/* Paginación del servidor */}
          <SuiBox display="flex" justifyContent="center" mt={2} mb={1}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
            />
          </SuiBox>
        </Paper>
      )}

      {/* Menú contextual */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {(roles.isAdmin || roles.isCartera || roles.isOperador || roles.isDirector) && (
          <>
            <MenuItem onClick={() => handleSoportes(planesPago.find((a) => a.id === selectedId))}>
              <AddCircleOutlineIcon title="SOPORTES" fontSize="small" sx={{ mr: 1 }} /> Soportes
            </MenuItem>
          </>
        )}
        {(roles.isAdmin || roles.isCartera || roles.isCoordinador || roles.isDirector) &&
          !selectedPlanPago?.estado && (
            <MenuItem
              onClick={() => handleIniciarPlan(planesPago.find((a) => a.id === selectedId))}
            >
              <StartIcon title="INICIAR PLAN" fontSize="small" sx={{ mr: 1 }} /> Iniciar Plan
            </MenuItem>
          )}
        {(roles.isAdmin || roles.isCartera || roles.isCoordinador || roles.isDirector) &&
          selectedPlanPago?.estado && (
            <MenuItem
              onClick={() => handleReiniciarPlan(planesPago.find((a) => a.id === selectedId))}
            >
              <RestoreIcon title="REINICIAR PLAN" fontSize="small" sx={{ mr: 1 }} /> Reiniciar Plan
            </MenuItem>
          )}
        {(roles.isAdmin || roles.isCartera || roles.isCoordinador || roles.isDirector) &&
          selectedPlanPago?.estado && (
            <MenuItem
              onClick={() => handleMovimientos(planesPago.find((a) => a.id === selectedId))}
            >
              <CurrencyExchangeIcon title="Balance" fontSize="small" sx={{ mr: 1 }} />
              Movimientos
            </MenuItem>
          )}
        {(roles.isAdmin || roles.isCartera || roles.isCoordinador || roles.isDirector) &&
          selectedPlanPago?.estado && (
            <MenuItem
              onClick={() => handleCambiarEstadoPlan(planesPago.find((a) => a.id === selectedId))}
            >
              {selectedPlanPago?.autorizado ? (
                <CancelIcon
                  title="DESAUTORIZAR PLAN"
                  fontSize="small"
                  sx={{ mr: 1 }}
                  color="error"
                />
              ) : (
                <CheckCircleIcon
                  title="AUTORIZAR PLAN"
                  fontSize="small"
                  sx={{ mr: 1 }}
                  color="success"
                />
              )}
              {selectedPlanPago?.autorizado ? "Desautorizar" : "Autorizar"}
            </MenuItem>
          )}
      </Menu>

      {/* Modales existentes */}
      <SoportesPlanPagoModal
        open={modalSoportesOpen}
        onClose={() => setModalSoportesOpen(false)}
        planPago={selectedPlanPago}
        onSuccess={() => {
          reloadCurrentPage();
          setModalSoportesOpen(false);
        }}
      />
      <IniciarPlanPago
        open={modalIniciarPlan}
        onClose={() => setModalIniciarPlan(false)}
        plan_pago={selectedPlanPago}
        onSuccess={() => {
          reloadCurrentPage();
          setModalIniciarPlan(false);
        }}
      />

      <BalanceModal
        open={modalMovimientosOpen}
        onClose={() => {
          reloadCurrentPage();
          setModalMovimientosOpen(false);
        }}
        plan_pago={selectedPlanPago}
        onPlanPagoChange={handleActualizarPlanPago}
      />
    </SuiBox>
  );
};

export default PlanesPagoTable;
